import React from "react";
import { Link } from "react-router-dom";
import {
  Fingerprint,
  Dices,
  UserSearch,
  Users,
  FileCode2,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Zap,
  Gift,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";

const tools = [
  {
    icon: Fingerprint,
    to: "/tools/npi-validator",
    testid: "tools-card-npi-validator",
    tag: "Most popular",
    title: "NPI Validator",
    desc: "Check if any 10-digit NPI is a valid NPI number — runs the CMS Luhn checksum and pulls live provider details (name, taxonomy, address, status) from the CMS NPPES registry. Single or bulk up to 100, with CSV export.",
    bullets: ["CMS Luhn checksum + live NPPES lookup", "Bulk validation up to 100 NPIs", "One-click CSV export"],
  },
  {
    icon: Dices,
    to: "/tools/fake-npi-generator",
    testid: "tools-card-fake-npi-generator",
    tag: "For developers & QA",
    title: "Fake NPI Generator",
    desc: "Generate synthetic, checksum-valid NPI numbers for development, testing, training, and demos. Every number passes the CMS Luhn checksum but is randomly generated — realistic test data with zero compliance risk.",
    bullets: ["Checksum-valid 10-digit NPIs", "Batches up to 100, uniqueness guaranteed", "Copy or export to CSV"],
  },
  {
    icon: Users,
    to: "/tools/fake-patient-generator",
    testid: "tools-card-fake-patient-generator",
    tag: "Test data",
    title: "Fake Patient Generator",
    desc: "Generate synthetic patient records — names, DOBs, member IDs, group numbers, never-issued SSNs, addresses, and fictional phone numbers. Realistic test data for claims, QA, training, and demos.",
    bullets: ["Never-issued 9xx SSNs & 555 phone numbers", "Batches up to 100, uniqueness guaranteed", "Copy as JSON or export to CSV"],
  },
  {
    icon: FileCode2,
    to: "/tools/payer-specific-synthetic-837",
    testid: "tools-card-payer-specific-synthetic-837",
    tag: "EDI test files",
    title: "Synthetic 837 Generator",
    desc: "Generate complete, structurally valid X12 837P, 837I, and 837D test claim files with payer-specific envelopes, synthetic patients, and checksum-valid NPIs. Download as .edi for clearinghouse certification and pipeline QA.",
    bullets: ["837P / 837I / 837D versions", "Payer-specific ISA/GS envelopes", "Balanced claims, ready to download"],
  },
  {
    icon: UserSearch,
    to: "/tools/npi-lookup-by-name",
    testid: "tools-card-npi-lookup-by-name",
    tag: "Registry search",
    title: "NPI Lookup by Name",
    desc: "Find any US healthcare provider or organization's NPI by name. Searches the official CMS NPPES registry live and returns NPI, entity type, taxonomy, and practice address — free and instant.",
    bullets: ["Live CMS NPPES search", "Individuals and organizations", "Full provider details returned"],
  },
];

const faqs = [
  {
    q: "Are Hewto.ai's NPI tools free?",
    a: "Yes — all four tools are 100% free with no signup and no API key. The NPI Validator and NPI Lookup by Name query the official CMS NPPES public registry in real time, and the Fake NPI Generator and Fake Patient Generator run entirely in your browser.",
  },
  {
    q: "What NPI tools does Hewto.ai offer?",
    a: "Hewto.ai offers four free tools: an NPI Validator (CMS Luhn checksum + live NPPES lookup, single or bulk with CSV export), a Fake NPI Generator (synthetic checksum-valid NPIs for testing and demos), a Fake Patient Generator (synthetic patient records with never-issued SSNs for claims testing), and an NPI Lookup by Name (find any provider or organization's NPI in the NPPES registry).",
  },
  {
    q: "Where does the NPI data come from?",
    a: "The NPI Validator and NPI Lookup by Name pull data live from the CMS NPPES NPI Registry (National Plan and Provider Enumeration System) — the official US registry maintained by the Centers for Medicare & Medicaid Services. The Fake NPI Generator creates synthetic numbers locally in your browser; nothing is sent to or stored on any server.",
  },
  {
    q: "Which tool should I use to check if an NPI is real?",
    a: "Use the NPI Validator. It runs the CMS Luhn checksum and then looks the number up live in the NPPES registry, returning the provider's name, taxonomy, address, and active status — so you can confirm an NPI is real before it goes on a claim.",
  },
  {
    q: "Which tool should I use for test NPI data?",
    a: "Use the Fake NPI Generator. It creates checksum-valid 10-digit NPIs that behave like real ones in format checks but are randomly generated — safe for development, QA, EDI 837 testing, training, and demos. Never use generated NPIs on a real claim.",
  },
  {
    q: "Do these tools store the NPIs I look up or generate?",
    a: "No. Registry lookups are proxied in real time and never persisted, and fake NPI generation happens entirely in your browser. Nothing you enter or generate is stored by Hewto.ai.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const itemListJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Free NPI Tools by Hewto.ai",
  description:
    "Free NPI tools for US healthcare: NPI validator with CMS NPPES lookup, fake NPI generator for testing, and NPI lookup by name.",
  itemListElement: tools.map((t, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: t.title,
    url: `https://www.hewto.ai${t.to}`,
  })),
};

const ToolsIndex = () => (
  <PageTransition>
    <Seo
      title="Free NPI Tools — NPI Validator, Fake NPI Generator & NPI Lookup | Hewto.ai"
      description="Free NPI tools for US healthcare by Hewto.ai: validate any NPI against the CMS NPPES registry (single or bulk with CSV export), generate checksum-valid fake NPI numbers for testing and demos, and look up any provider's NPI by name. No signup, no API key."
      keywords="npi tools, free npi tools, npi validator, npi lookup, npi lookup by name, fake npi generator, test npi numbers, npi number validation, nppes lookup, cms npi lookup, npi checker, bulk npi validator, healthcare provider lookup, npi test data, npi verification tool, free npi checker"
      canonical="https://www.hewto.ai/tools"
      jsonLd={[faqJsonLd, itemListJsonLd]}
    />

    <PageHero
      eyebrow="Free tools · NPI toolkit"
      title="Free NPI tools for US healthcare"
      subtitle="Four free tools from Hewto.ai to work with healthcare identifiers and test data: validate NPIs against the live CMS NPPES registry, generate checksum-valid fake NPIs, create synthetic patient records, and look up any provider's NPI by name. No signup, no API key, nothing stored."
    />

    {/* TOOL CARDS */}
    <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="tools-grid">
        {tools.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.08}>
            <Link
              to={t.to}
              data-testid={t.testid}
              className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brand hover:shadow-xl hover:shadow-brand/10"
            >
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                  <t.icon className="h-6 w-6" />
                </span>
                <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
                  {t.tag}
                </span>
              </div>
              <h2 className="mt-5 text-xl font-semibold text-slate-900">{t.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.desc}</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-brand">
                Open tool
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>

    {/* WHY FREE TOOLS */}
    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/40 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
              <Gift className="h-3.5 w-3.5" /> Free forever
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Why we built these NPI tools
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Hewto.ai automates healthcare document processing — claims, enrollment forms, and
              EDI. NPIs sit at the heart of all of it, so we built the tools we wished existed and
              made them free for the whole industry.
            </p>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              icon: Zap,
              title: "Instant, no friction",
              desc: "No accounts, no API keys, no rate-limit anxiety. Open a tool, get your answer, get back to work.",
            },
            {
              icon: ShieldCheck,
              title: "Official data sources",
              desc: "Lookups hit the CMS NPPES registry live — the same source payers, clearinghouses, and RCM platforms trust.",
            },
            {
              icon: Sparkles,
              title: "Built by claims people",
              desc: "The same team behind Hewto.ai's claim-form extraction platform — these tools follow the exact CMS rules your billing software enforces.",
            },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <f.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
      <Reveal>
        <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
          NPI tools FAQ
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Common questions about our free NPI tools
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-8" data-testid="tools-faq">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger
                data-testid={`tools-faq-trigger-${i}`}
                className="text-left text-base font-semibold text-slate-900 hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>

    <CTABand />
  </PageTransition>
);

export default ToolsIndex;
